import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  EditableStepDetailContent,
  StepDetailBody,
  StepDetailHeader,
  StepDetailPanel,
  StepModeSwitch,
  useDebouncedFieldUpdate,
  type EditableStepPatch,
  type StepMode
} from '@bliks-io/ui';
import type { ProcessDraftStep, ProcessStepRecordingDisplay } from '@bliks-io/contracts';
import { useAiEnabled } from '@/hooks/useAiEnabled';
import { useProcessDraft } from '@/api/queries/useProcessDraft';
import { WhoElseDoesThisPicker } from '@/components/entity-pickers/who-else/WhoElseDoesThisPicker';
import { assigneeFieldVisibility } from '@/lib/assigneeFieldVisibility';
import { EditorSuccessorPicker } from '../../components/EditorSuccessorPicker';
import { EditorHelpersSlot, type EditorHelpers } from '../../components/EditorHelpersSlot';
import { getEntityRefId } from '@/lib/entityRefUtils';
import { useAssigneeLookup } from '@/lib/useAssigneeLookup';
import { useDialogOptionsForDraftEditor } from '../adapters/dialogOptions';
import { useUndoableEditorActions } from '../actions/useUndoableEditorActions';
import { useProcessDraftCache } from '../actions/useProcessDraftCache';
import { hasAssignmentAnchorInput, hasDownstream, startEventIdsOf } from '../edge-actions/shared';
import {
  syncWhoElseOnAssigneeChange,
  whoElseAssigneeExcludeIds
} from '../../components/useWhoElseUserSelect';
import { useDraftManualStepHandlers } from './useDraftManualStepHandlers';
import { DraftStepSurveySection } from './DraftStepSurveySection';
import { DraftStepCapacitySection } from './DraftStepCapacitySection';
import { DraftStepAiNameButton } from './DraftStepAiNameButton';
import { DraftIssueSummary } from './DraftIssueSummary';
import { useNodeFieldIssues, useNodeInvalidRuleIndexes } from './useNodeFieldIssues';

interface Props {
  step: ProcessDraftStep;
  processId: string;
  siblingSteps: ProcessStepRecordingDisplay[];
  onClose: () => void;
}

/**
 * Side-panel for a manual `ProcessDraftStep`. Assignments are handled by
 * `DraftAssignmentPanel`; the dispatcher `DraftStepPanel` picks which to
 * render based on which collection the selected id lives in.
 */
export function DraftManualStepPanel({ step, processId, siblingSteps, onClose }: Props) {
  const { t } = useTranslation();
  const { stepNameGenerationEnabled: aiForStepNames } = useAiEnabled();
  const { convertStepToAssignment } = useUndoableEditorActions({ processId });
  const { getCachedSteps, getCachedDraftAssignments, getCachedEvents } =
    useProcessDraftCache(processId);
  const { intermediateEventTypes } = useDialogOptionsForDraftEditor();
  const assigneeLookup = useAssigneeLookup();
  const fieldIssues = useNodeFieldIssues(String(step.stableStepId));
  const invalidRuleIndexes = useNodeInvalidRuleIndexes(String(step.stableStepId));

  const {
    handlePatch,
    handleSetResurveyRequested,
    handleAddSuccessorSteps,
    handleRemoveSuccessorStep,
    handleMarkAsTerminal,
    handleMarkRuleAsTerminal,
    handleAddRuleSuccessorSteps,
    handleRemoveRuleSuccessorStep
  } = useDraftManualStepHandlers(step, processId);

  // Re-survey only applies to steps with human performers; System/ExternalContact
  // steps never carry surveys, so the toggle is hidden for them.
  const surveyEligible =
    step.assignedToModel !== 'System' && step.assignedToModel !== 'ExternalContact';

  // Active-survey counts for this step (ADR 0009) so the user sees what a
  // re-request would refresh. Read from the cached draft query.
  const draftData = useProcessDraft(processId).data;
  const surveyStatus =
    draftData && draftData !== 'not-found'
      ? draftData.surveyStatus?.[String(step.stableStepId)]
      : undefined;

  const nameField = useDebouncedFieldUpdate({
    value: step.generatedName ?? '',
    onCommit: (next) => void handlePatch({ generatedName: next.trim() ? next : null })
  });

  const descriptionField = useDebouncedFieldUpdate({
    value: step.generatedDescription ?? '',
    onCommit: (next) => void handlePatch({ generatedDescription: next.trim() ? next : null })
  });

  const stepIndex = siblingSteps.findIndex((s) => s._id === String(step.stableStepId));
  const displayStep = siblingSteps[stepIndex];

  const whoElseExcludeIds = whoElseAssigneeExcludeIds({
    id: getEntityRefId(step.assignedTo),
    model: step.assignedToModel
  });
  const fieldVisibility = assigneeFieldVisibility(step.assignedToModel);

  const handlePatchWithWhoElseSync = useCallback(
    (patch: EditableStepPatch) =>
      handlePatch(syncWhoElseOnAssigneeChange(patch, displayStep?.whoElseDoesThis)),
    [handlePatch, displayStep?.whoElseDoesThis]
  );

  const helpers: EditorHelpers = {
    persons: step.helpers?.persons ?? [],
    orgFunctions: step.helpers?.orgFunctions ?? [],
    systems: step.helpers?.systems ?? [],
    documents: step.helpers?.documents ?? []
  };

  const handleModeChange = useCallback(
    (next: StepMode) => {
      if (next === 'viral') void convertStepToAssignment(String(step.stableStepId));
    },
    [convertStepToAssignment, step.stableStepId]
  );

  // Mirrors the context-menu gating for "Convert to viral": only a leaf step
  // with an upstream anchor input can become an assignment, since downstream
  // work has no owning task to resume on undo otherwise.
  const canConvertToAssignment = useMemo(() => {
    const parentStable = String(step.stableStepId);
    if (!hasAssignmentAnchorInput(step.inputs, startEventIdsOf({ getCachedEvents }))) return false;
    return !hasDownstream(parentStable, getCachedSteps(), getCachedDraftAssignments());
  }, [step.stableStepId, step.inputs, getCachedEvents, getCachedSteps, getCachedDraftAssignments]);

  return (
    <StepDetailPanel>
      <StepDetailHeader
        step={displayStep}
        onClose={onClose}
        stepNumber={displayStep?.stepNumber}
        editableName={{
          value: nameField.value,
          onChange: nameField.onChange,
          onBlur: nameField.flush,
          placeholder: t('processFlow.stepPrefix', {
            number: displayStep?.stepNumber ?? stepIndex + 1
          })
        }}
        editableDescription={{
          value: descriptionField.value,
          onChange: descriptionField.onChange,
          onBlur: descriptionField.flush,
          placeholder: t('processDraft.editor.descriptionPlaceholder')
        }}
        headerAction={
          aiForStepNames ? (
            <DraftStepAiNameButton step={step} processId={processId} onPatch={handlePatch} />
          ) : undefined
        }
      />
      <StepDetailBody>
        <DraftIssueSummary nodeId={String(step.stableStepId)} />
        <StepModeSwitch
          mode="manual"
          onChange={handleModeChange}
          labels={{
            manual: t('processDraft.mode.manual'),
            viral: t('processDraft.mode.viral')
          }}
          disabledReason={
            canConvertToAssignment
              ? undefined
              : { viral: t('processDraft.mode.viralDisabledReason') }
          }
        />
        <EditableStepDetailContent
          step={displayStep}
          mode="draft"
          onPatchStep={handlePatchWithWhoElseSync}
          fieldIssues={fieldIssues}
          invalidRuleIndexes={invalidRuleIndexes}
          successorActions={{
            addSteps: handleAddSuccessorSteps,
            removeStep: handleRemoveSuccessorStep,
            markTerminal: handleMarkAsTerminal,
            addRuleSteps: handleAddRuleSuccessorSteps,
            removeRuleStep: handleRemoveRuleSuccessorStep,
            markRuleTerminal: handleMarkRuleAsTerminal
          }}
          assigneeLookup={assigneeLookup}
          eventTypes={intermediateEventTypes}
          processSteps={siblingSteps}
          renderSuccessorPicker={(pickerProps) => (
            <EditorSuccessorPicker
              {...pickerProps}
              processId={processId}
              recordingId={String(step.stableStepId)}
              pendingSuggestions={displayStep?.entitySuggestions}
            />
          )}
          fieldSlots={{
            ...(fieldVisibility.helpers
              ? {
                  helpers: (
                    <EditorHelpersSlot
                      helpers={helpers}
                      processId={processId}
                      recordingId={String(step.stableStepId)}
                      entitySuggestions={displayStep?.entitySuggestions}
                      onChange={(next) => void handlePatch({ helpers: next })}
                    />
                  )
                }
              : {}),
            ...(fieldVisibility.whoElse
              ? {
                  whoElseDoesThis: (
                    <WhoElseDoesThisPicker
                      value={displayStep?.whoElseDoesThis ?? []}
                      onChange={(next) => void handlePatch({ whoElseDoesThis: next })}
                      excludeIds={whoElseExcludeIds}
                      processId={processId}
                    />
                  )
                }
              : {})
          }}
        />
        {surveyEligible && (
          <DraftStepSurveySection
            resurveyRequested={step.resurveyRequested ?? false}
            surveyStatus={surveyStatus}
            onToggleResurvey={(next) => void handleSetResurveyRequested(next)}
          />
        )}
        {surveyEligible && <DraftStepCapacitySection step={step} processId={processId} />}
      </StepDetailBody>
    </StepDetailPanel>
  );
}
